import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import UpgradeModal from "@/components/UpgradeModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Check, Play, Trash2, Sparkles, Save, X } from "lucide-react";

export default function UploadExam() {
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload | parsing | confirm
  const [file, setFile] = useState(null);
  const [examId, setExamId] = useState(null);
  const [structure, setStructure] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [marksCorrect, setMarksCorrect] = useState("4");
  const [marksWrong, setMarksWrong] = useState("-1");
  const [marksPartial, setMarksPartial] = useState("0");
  const [userPresets, setUserPresets] = useState([]);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const inputRef = useRef();

  useEffect(() => {
    api.get("/user/quota").then((r) => setQuota(r.data)).catch(() => {});
    api.get("/user/presets").then((r) => setUserPresets(r.data.presets || [])).catch(() => {});
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/exams/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExamId(r.data.exam_id);
      setStep("parsing");
      // parse
      toast.info("AI is analysing your paper…", { duration: 4000 });
      const p = await api.post(`/exams/${r.data.exam_id}/parse`);
      setStructure(p.data.structure);
      setStep("confirm");
      toast.success(`Detected ${p.data.structure.questions.length} questions`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowUpgrade(true);
        setStep("upload");
      } else {
        toast.error(e?.response?.data?.detail || "Upload failed");
        setStep("upload");
      }
    } finally {
      setUploading(false);
    }
  };

  const saveAndStart = async () => {
    try {
      await api.patch(`/exams/${examId}`, { structure });
      const r = await api.post("/attempts/start", { exam_id: examId });
      navigate(`/exam/${examId}?attempt=${r.data.attempt_id}`);
    } catch (e) {
      toast.error("Could not start exam");
    }
  };

  const updateQ = (idx, field, value) => {
    const next = { ...structure };
    const updated = { ...next.questions[idx], [field]: value };
    if (field === "q_type") {
      // Auto-zero negative marks when switching to numeric/one-word type
      if (value === "integer" || value === "one_word") {
        if ((updated.marks_wrong || 0) < 0) updated.marks_wrong = 0;
        updated.marks_partial = 0;
      }
      if (value === "mcq_multi" && !updated.marks_partial) {
        updated.marks_partial = 1;
      }
      if (value === "mcq") {
        updated.marks_partial = 0;
      }
    }
    next.questions[idx] = updated;
    setStructure(next);
  };

  const uniqueSections = useMemo(() => {
    if (!structure) return [];
    const seen = new Set();
    const arr = [];
    structure.questions.forEach((q) => {
      const s = q.section || "Section 1";
      if (!seen.has(s)) { seen.add(s); arr.push(s); }
    });
    return arr;
  }, [structure]);

  const applyScheme = (targetSection, c, w, p) => {
    if (isNaN(c) || isNaN(w) || isNaN(p)) return 0;
    const next = { ...structure };
    let count = 0;
    next.questions = next.questions.map((q) => {
      if (targetSection && q.section !== targetSection) return q;
      count++;
      const isMcq = q.q_type === "mcq";
      const isMulti = q.q_type === "mcq_multi";
      const isNumeric = q.q_type === "integer" || q.q_type === "one_word";
      return {
        ...q,
        marks_correct: c,
        marks_wrong: isNumeric ? 0 : w,
        marks_partial: isMulti ? p : 0,
        // keep isMcq for lint; MCQs use marks_wrong as-is
        ...(isMcq ? {} : {}),
      };
    });
    next.total_marks = next.questions.reduce((s, q) => s + (q.marks_correct || 0), 0);
    setStructure(next);
    return count;
  };

  const applyGlobal = () => {
    const c = parseFloat(marksCorrect);
    const w = parseFloat(marksWrong);
    const p = parseFloat(marksPartial);
    const n = applyScheme(null, c, w, p);
    if (n === 0) return;
    toast.success(`Applied +${c} / ${w} / +${p} to ${n} question${n > 1 ? "s" : ""}`);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    const c = parseFloat(marksCorrect);
    const w = parseFloat(marksWrong);
    const p = parseFloat(marksPartial);
    if (isNaN(c) || isNaN(w) || isNaN(p)) { toast.error("Enter valid numbers"); return; }
    try {
      const r = await api.post("/user/presets", {
        name, marks_correct: c, marks_wrong: w, marks_partial: p, q_type: "mcq",
      });
      // upsert into local list
      setUserPresets((prev) => {
        const filtered = prev.filter((x) => x.name !== r.data.preset.name);
        return [r.data.preset, ...filtered];
      });
      toast.success(`Saved preset "${name}"`);
      setSavePresetOpen(false);
      setPresetName("");
    } catch (e) {
      toast.error("Could not save preset");
    }
  };

  const deletePreset = async (preset_id) => {
    try {
      await api.delete(`/user/presets/${preset_id}`);
      setUserPresets((prev) => prev.filter((p) => p.preset_id !== preset_id));
      toast.success("Preset removed");
    } catch {
      toast.error("Could not delete preset");
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8 fade-up">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Step {step === "confirm" ? "2" : "1"} of 2</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">
              {step === "confirm" ? "Confirm exam structure" : "Upload exam paper"}
            </h1>
            <p className="text-stone-500 mt-2">
              {step === "confirm"
                ? "Review what the AI extracted. Edit anything that looks off. Then start."
                : "PDF only. MCQ and one-word/integer answer papers supported for MVP."}
            </p>
          </div>
          {quota && !quota.is_premium && (
            <div
              className="rounded-md border border-stone-200 bg-white px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-orange-400 transition-colors"
              onClick={() => setShowUpgrade(true)}
              data-testid="quota-badge"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Sparkles className="h-4 w-4 text-orange-600" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">This month</div>
                <div className="font-mono text-sm font-semibold tabular-nums">
                  {quota.used} / {quota.limit} exams · <span className="text-orange-600">Upgrade</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {step === "upload" && (
          <Card className="p-10 rounded-md border-stone-200 shadow-none">
            <div
              className="border-2 border-dashed border-stone-300 rounded-md p-12 text-center hover:border-orange-500 transition-colors cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f && f.type === "application/pdf") setFile(f);
              }}
              data-testid="upload-dropzone"
            >
              <Upload className="h-12 w-12 mx-auto text-stone-400 mb-4" />
              <div className="font-heading text-xl font-semibold mb-2">
                {file ? file.name : "Drop your PDF here"}
              </div>
              <p className="text-sm text-stone-500 mb-4">or click to browse</p>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="file-input"
                onChange={(e) => setFile(e.target.files?.[0])}
              />
              {file && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </Badge>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <Button
                disabled={!file || uploading}
                onClick={handleUpload}
                data-testid="upload-btn"
                className="bg-orange-600 hover:bg-orange-700 text-white h-11"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Upload & parse
              </Button>
            </div>
          </Card>
        )}

        {step === "parsing" && (
          <Card className="p-16 rounded-md border-stone-200 shadow-none text-center">
            <Loader2 className="h-12 w-12 mx-auto text-orange-600 animate-spin mb-4" />
            <div className="font-heading text-2xl font-semibold mb-2">Analysing your paper</div>
            <p className="text-stone-500">
              Gemini 3 Flash is detecting sections, marks, question types & negative marking. This can take up to a minute.
            </p>
          </Card>
        )}

        {step === "confirm" && structure && (
          <div className="space-y-6">
            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Title</Label>
                  <Input
                    value={structure.title}
                    onChange={(e) => setStructure({ ...structure, title: e.target.value })}
                    data-testid="exam-title-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Total time (min)</Label>
                  <Input
                    type="number"
                    value={structure.total_time_minutes}
                    onChange={(e) => setStructure({ ...structure, total_time_minutes: parseInt(e.target.value) || 60 })}
                    data-testid="exam-time-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Total questions</Label>
                  <div className="h-10 flex items-center font-mono text-lg font-semibold">{structure.questions.length}</div>
                </div>
              </div>
            </Card>

            {/* Marking scheme */}
            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold">Marking scheme</h3>
                  <p className="text-xs text-stone-500 mt-1">
                    Set the global scheme, then override per-section below. Numeric answers auto-use 0 negative; partial applies only to multi-correct MCQs.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSavePresetOpen(true)}
                  className="border-stone-300"
                  data-testid="save-preset-btn"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Save as preset
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    +Correct
                  </Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={marksCorrect}
                    onChange={(e) => setMarksCorrect(e.target.value)}
                    data-testid="marks-correct-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    −Wrong (MCQ)
                  </Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={marksWrong}
                    onChange={(e) => setMarksWrong(e.target.value)}
                    data-testid="marks-wrong-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    +Partial (multi)
                  </Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={marksPartial}
                    onChange={(e) => setMarksPartial(e.target.value)}
                    data-testid="marks-partial-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={applyGlobal}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  data-testid="apply-marks-btn"
                >
                  Apply to all
                </Button>
                <div className="text-xs text-stone-500 font-mono">
                  Presets:
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {[
                      { c: 4, w: -1, p: 0, l: "JEE 4/-1" },
                      { c: 4, w: -2, p: 1, l: "JEE-Adv 4/-2/+1" },
                      { c: 4, w: 0, p: 0, l: "NEET 4/0" },
                      { c: 1, w: 0, p: 0, l: "SAT 1/0" },
                      { c: 1, w: -0.25, p: 0, l: "GRE 1/-0.25" },
                    ].map((p) => (
                      <button
                        key={p.l}
                        type="button"
                        onClick={() => {
                          setMarksCorrect(String(p.c));
                          setMarksWrong(String(p.w));
                          setMarksPartial(String(p.p));
                        }}
                        className="rounded border border-stone-300 px-2 py-0.5 text-[10px] hover:border-orange-500 hover:text-orange-600"
                        style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}
                        data-testid={`preset-${p.l.split(" ")[0]}`}
                      >
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {userPresets.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-2">
                    Your presets
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="user-presets">
                    {userPresets.map((up) => (
                      <div
                        key={up.preset_id}
                        className="inline-flex items-center gap-1 rounded border border-stone-300 pl-2 pr-1 py-0.5 text-[11px] font-mono hover:border-orange-500"
                        style={{ transition: "border-color 0.15s ease" }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setMarksCorrect(String(up.marks_correct));
                            setMarksWrong(String(up.marks_wrong));
                            setMarksPartial(String(up.marks_partial || 0));
                            toast.success(`Loaded "${up.name}"`);
                          }}
                          className="hover:text-orange-600"
                          data-testid={`user-preset-${up.preset_id}`}
                        >
                          {up.name}
                          <span className="text-stone-400 ml-1">
                            {up.marks_correct}/{up.marks_wrong}
                            {up.marks_partial ? `/+${up.marks_partial}` : ""}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => deletePreset(up.preset_id)}
                          className="text-stone-400 hover:text-rose-600 ml-1"
                          aria-label={`Delete ${up.name}`}
                          data-testid={`delete-preset-${up.preset_id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Per-section overrides */}
            {uniqueSections.length > 1 && (
              <Card className="p-6 rounded-md border-stone-200 shadow-none">
                <div className="mb-4">
                  <h3 className="font-heading text-lg font-semibold">Per-section overrides</h3>
                  <p className="text-xs text-stone-500 mt-1">
                    Different subjects often use different schemes. Apply per-section marks with one click.
                  </p>
                </div>
                <div className="space-y-2" data-testid="section-scheme-list">
                  {uniqueSections.map((sec) => (
                    <SectionSchemeRow
                      key={sec}
                      section={sec}
                      count={structure.questions.filter((q) => q.section === sec).length}
                      onApply={(c, w, p) => {
                        const n = applyScheme(sec, c, w, p);
                        toast.success(`Applied to ${n} question${n > 1 ? "s" : ""} in "${sec}"`);
                      }}
                    />
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold">Questions ({structure.questions.length})</h3>
                <Badge variant="outline" className="font-mono text-xs">Edit as needed</Badge>
              </div>
              <div className="max-h-[500px] overflow-y-auto space-y-2 pr-2">
                {structure.questions.map((q, idx) => (
                  <div key={q.q_id} className="border border-stone-200 rounded-md p-4">
                    <div className="flex items-start gap-3 mb-2">
                      <span className="font-mono text-sm font-semibold text-stone-500 w-8">Q{q.number}</span>
                      <div className="flex-1">
                        <textarea
                          className="w-full text-sm bg-transparent border-none focus:outline-none resize-none"
                          rows={2}
                          value={q.text}
                          onChange={(e) => updateQ(idx, "text", e.target.value)}
                          data-testid={`q-text-${idx}`}
                        />
                        {(q.q_type === "mcq" || q.q_type === "mcq_multi") && q.options.length > 0 && (
                          <div className="grid grid-cols-2 gap-1 mt-2">
                            {q.options.map((opt, i) => (
                              <div key={`${q.q_id}-opt-${i}`} className="font-mono text-xs text-stone-600 flex gap-2">
                                <span className="font-semibold">{String.fromCharCode(65 + i)}.</span>
                                <span className="truncate">{opt}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 items-end min-w-[160px]">
                        <Select value={q.q_type} onValueChange={(v) => updateQ(idx, "q_type", v)}>
                          <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mcq">MCQ (single)</SelectItem>
                            <SelectItem value="mcq_multi">MCQ (multi)</SelectItem>
                            <SelectItem value="integer">Integer</SelectItem>
                            <SelectItem value="one_word">One-word</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="font-mono text-[10px] text-stone-500">
                          +{q.marks_correct} / {q.marks_wrong}
                          {q.q_type === "mcq_multi" && q.marks_partial ? ` / +${q.marks_partial}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep("upload")} data-testid="back-btn">Re-upload</Button>
              <Button
                onClick={saveAndStart}
                data-testid="start-exam-btn"
                className="bg-orange-600 hover:bg-orange-700 text-white h-11"
              >
                <Play className="h-4 w-4 mr-2" />
                Save & start exam
              </Button>
            </div>
          </div>
        )}
      </div>
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} quota={quota} />

      {/* Save-preset dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">Save marking preset</DialogTitle>
            <DialogDescription>
              Reuse this scheme for future papers in one click.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Name</Label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g. My coaching batch"
                maxLength={40}
                data-testid="preset-name-input"
              />
            </div>
            <div className="text-xs text-stone-500 font-mono">
              Saving: +{marksCorrect} / {marksWrong}
              {parseFloat(marksPartial) ? ` / +${marksPartial}` : ""}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetOpen(false)}>Cancel</Button>
            <Button
              onClick={savePreset}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="save-preset-confirm-btn"
            >
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function SectionSchemeRow({ section, count, onApply }) {
  const [c, setC] = useState("4");
  const [w, setW] = useState("-1");
  const [p, setP] = useState("0");
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end border border-stone-200 rounded-md p-3"
      data-testid={`section-scheme-${section}`}
    >
      <div className="md:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Section</div>
        <div className="font-heading text-sm font-semibold truncate">{section}</div>
        <div className="font-mono text-[10px] text-stone-400 mt-0.5">{count} question{count > 1 ? "s" : ""}</div>
      </div>
      <div>
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">+Correct</Label>
        <Input type="number" step="0.25" value={c} onChange={(e) => setC(e.target.value)} className="h-8 text-sm" />
      </div>
      <div>
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">−Wrong</Label>
        <Input type="number" step="0.25" value={w} onChange={(e) => setW(e.target.value)} className="h-8 text-sm" />
      </div>
      <div>
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">+Partial</Label>
        <Input type="number" step="0.25" value={p} onChange={(e) => setP(e.target.value)} className="h-8 text-sm" />
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          const cf = parseFloat(c); const wf = parseFloat(w); const pf = parseFloat(p);
          if (isNaN(cf) || isNaN(wf) || isNaN(pf)) { toast.error("Enter valid numbers"); return; }
          onApply(cf, wf, pf);
        }}
        className="border-orange-500 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
        data-testid={`apply-section-${section}`}
      >
        Apply
      </Button>
    </div>
  );
}
